import { PlusCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { AgentsIcon } from '@/components/icons';
import type { NoDataProps } from '@/components/legacy-ui/NoData';
import { createClearFiltersButton } from '@/components/legacy-ui/utils/createClearFiltersButton';
import { Button } from '@/components/ui/button';

/**
 * No-data configuration for agents table
 *
 * Returns appropriate NoData config based on state:
 * 1. Error state - Connection issue with retry/setup actions
 * 2. Empty state (isAllEmpty) - No agents configured yet
 * 3. Filtered empty - No agents match current filter
 */
export const getNoDataConfig = (
  isAllEmpty: boolean,
  hasError: boolean,
  onNewAgent: () => void,
  onRetry?: () => void,
  onClearFilters?: () => void,
  aegisProvision?: {
    onProvision: () => void;
    isProvisioning: boolean;
  }
): NoDataProps => {
  // Aegis not provisioned — Praetorian-only action to set up Aegis enrollment
  if (hasError && aegisProvision) {
    return {
      icon: <AgentsIcon className="text-chariot-stroke size-20" />,
      title: 'Aegis Not Provisioned',
      description:
        'Aegis enrollment has not been configured for this account. Provision it to enable endpoint management.',
      action: (
        <div className="flex gap-3">
          <Button
            onClick={aegisProvision.onProvision}
            size="sm"
            disabled={aegisProvision.isProvisioning}
          >
            {aegisProvision.isProvisioning
              ? 'Provisioning…'
              : 'Provision Aegis'}
          </Button>
        </div>
      ),
    };
  }

  // Error State (highest priority) - uses friendly icon like empty state
  if (hasError) {
    return {
      icon: <AgentsIcon className="text-chariot-stroke size-20" />,
      title: 'Unable to Connect',
      description:
        'No endpoints identified. Please add your first endpoint or check your connection.',
      action: (
        <div className="flex gap-3">
          {onRetry && (
            <Button onClick={onRetry} variant="outline" size="sm">
              Retry
            </Button>
          )}
          <Button onClick={onNewAgent} size="sm">
            Add Endpoint
          </Button>
        </div>
      ),
    };
  }

  // All Empty State - No agents connected
  if (isAllEmpty) {
    return {
      icon: <AgentsIcon className="text-chariot-stroke size-20" />,
      title: 'No Endpoints Connected',
      description:
        'Connect your first endpoint to start monitoring your internal intranet attack surface',
      action: (
        <Button size="sm" onClick={onNewAgent} className="px-4">
          <PlusCircleIcon className="size-4" />
          Add Endpoint
        </Button>
      ),
    };
  }

  // Filtered Empty State - Search/filter returned no results
  return {
    icon: <AgentsIcon className="text-chariot-stroke size-20" />,
    title: 'No Endpoints match your current filter',
    action: createClearFiltersButton({
      hasActiveFilters: !!onClearFilters,
      onClearFilters: onClearFilters || (() => {}),
    }),
  };
};
